import { ChecklistItem } from '@app/app/_components/ChecklistItem/ChecklistItem';
import { Heading } from '@app/app/_components/Heading/Heading';
import { ChoiceButtonOption } from '@app/app/_components/Input/ChoiceButtonInput';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { Tag } from '@app/app/_components/Tag/Tag';
import { TransportPremiumsResponse } from '@app/domain/model/eshop/transport/TransportPremiumsResponse';
import { PremiumCode } from '@app/domain/model/eshop/transport/PremiumCode';
import { formatEuroValue } from '@app/infrastructure/application/formatEuroValue';
import { splitNumber } from '@app/lib/split-number/splitNumber';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { ArrowRightIcon } from '@app/app/_icons/ArrowRightIcon';
import { TextButton } from '@app/app/_components/TextButton/TextButton';
import { CascoRiskPackageCode } from '@app/domain/model/eshop/transport/CascoRiskPackageCode';
import { AppParameters } from '@app/infrastructure/parameters/model/AppParameters';

export const useCascoOptions = (
    premiums: TransportPremiumsResponse[],
    insuranceDuration: string,
    parameters: AppParameters,
): ChoiceButtonOption[] => {
    const t = useTranslations();

    const options = useMemo(() => {
        return premiums
            .filter(premium => premium.riskPackageCode !== PremiumCode.Mtpl)
            .map(premium => {
                const { code, title, premium: price, premiumAfterDiscount, riskPackageCode } = premium;

                const hasDiscount = premiumAfterDiscount > 0 && premiumAfterDiscount < price;
                let discountPercentage = 0;

                if (hasDiscount) {
                    discountPercentage = ((price - premiumAfterDiscount) / price) * 100;
                }

                const splittedPrice = splitNumber(price);
                const splittedPremiumAfterDiscount = splitNumber(premiumAfterDiscount);

                return {
                    value: code,
                    label: (
                        <div className="flex flex-col gap-3">
                            <div className="flex flex-row flex-wrap gap-3">
                                <Heading level="h3">{title}</Heading>
                                <Tag>{`${t('eshop.transport.save')} ${discountPercentage.toFixed(0)}%`}</Tag>
                            </div>
                            <div className="flex flex-row items-baseline gap-2">
                                {premiumAfterDiscount > 0 && premiumAfterDiscount < price ? (
                                    <div className="flex flex-col items-start">
                                        <Paragraph isGray size="tiny" className="line-through">
                                            {formatEuroValue(price, true)}
                                        </Paragraph>

                                        <div className="flex flex-row items-start gap-1">
                                            <Paragraph isBold size="medium">
                                                {splittedPremiumAfterDiscount.integerPart}
                                            </Paragraph>
                                            <Paragraph isBold size="tiny">
                                                {splittedPremiumAfterDiscount.fractionalPart}
                                            </Paragraph>
                                            <Paragraph isBold size="medium">
                                                €
                                            </Paragraph>
                                            <Paragraph
                                                className="self-center"
                                                isGray>{`/ ${insuranceDuration} ${t('general.months')}`}</Paragraph>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-row items-start gap-1">
                                        <Paragraph isBold size="tiny">
                                            €
                                        </Paragraph>
                                        <Paragraph isBold size="medium">
                                            {splittedPrice.integerPart}
                                        </Paragraph>
                                        <Paragraph isBold size="tiny">
                                            {splittedPrice.fractionalPart}
                                        </Paragraph>
                                        <Paragraph
                                            className="self-center"
                                            isGray>{`/ ${insuranceDuration} ${t('general.months')}`}</Paragraph>
                                    </div>
                                )}
                            </div>
                        </div>
                    ),
                    details: (
                        <div className="flex flex-col gap-4">
                            {riskPackageCode === CascoRiskPackageCode.Alternative && (
                                <div className="flex flex-col gap-3">
                                    <Paragraph isBold>
                                        {t('eshop.transport.cascoPackageOptions.mainRisksTitle')}
                                    </Paragraph>
                                    <div className="flex flex-col gap-1">
                                        <ChecklistItem>
                                            {t('eshop.transport.cascoPackageOptions.bsc.collision')}
                                        </ChecklistItem>
                                        <ChecklistItem>
                                            {t('eshop.transport.cascoPackageOptions.bsc.theft')}
                                        </ChecklistItem>
                                    </div>
                                    <TextButton
                                        preventDefault
                                        href={parameters.eshopInfoUrls.comparePolicies}
                                        target="_blank"
                                        rightAddon={<ArrowRightIcon className="size-4" />}>
                                        {t('general.readMore')}
                                    </TextButton>
                                </div>
                            )}

                            {riskPackageCode === CascoRiskPackageCode.Extended && (
                                <div className="flex flex-col gap-3">
                                    <Paragraph isBold>
                                        {t('eshop.transport.cascoPackageOptions.mainRisksTitle')}
                                    </Paragraph>
                                    <div className="flex flex-col gap-1">
                                        <ChecklistItem>
                                            {t('eshop.transport.cascoPackageOptions.super3.fire')}
                                        </ChecklistItem>
                                        <ChecklistItem>
                                            {t('eshop.transport.cascoPackageOptions.super3.collision')}
                                        </ChecklistItem>
                                        <ChecklistItem>
                                            {t('eshop.transport.cascoPackageOptions.super3.theft')}
                                        </ChecklistItem>
                                        <ChecklistItem>
                                            {t('eshop.transport.cascoPackageOptions.super3.vandalism')}
                                        </ChecklistItem>
                                        <ChecklistItem>
                                            {t('eshop.transport.cascoPackageOptions.super3.unexpected')}
                                        </ChecklistItem>
                                    </div>

                                    <Paragraph isBold>
                                        {t('eshop.transport.cascoPackageOptions.super3.extendedBenefitsTitle')}
                                    </Paragraph>
                                    <div className="flex flex-col gap-1">
                                        <ChecklistItem>
                                            {t('eshop.transport.cascoPackageOptions.super3.replacementCar')}
                                        </ChecklistItem>
                                        <ChecklistItem>
                                            {t('eshop.transport.cascoPackageOptions.super3.allGlass')}
                                        </ChecklistItem>
                                        <ChecklistItem>
                                            {t('eshop.transport.cascoPackageOptions.super3.naturalDisasters')}
                                        </ChecklistItem>
                                        <ChecklistItem>
                                            {t('eshop.transport.cascoPackageOptions.super3.animalCollision')}
                                        </ChecklistItem>
                                        <ChecklistItem>
                                            {t('eshop.transport.cascoPackageOptions.super3.chargingStation')}
                                        </ChecklistItem>
                                    </div>
                                    <TextButton
                                        preventDefault
                                        href={parameters.eshopInfoUrls.comparePolicies}
                                        target="_blank"
                                        rightAddon={<ArrowRightIcon className="size-4" />}>
                                        {t('general.readMore')}
                                    </TextButton>
                                </div>
                            )}

                            {riskPackageCode === CascoRiskPackageCode.Main && (
                                <div className="flex flex-col gap-3">
                                    <Paragraph isBold>
                                        {t('eshop.transport.cascoPackageOptions.mainRisksTitle')}
                                    </Paragraph>
                                    <div className="flex flex-col gap-1">
                                        <ChecklistItem>
                                            {t('eshop.transport.cascoPackageOptions.eko3.fire')}
                                        </ChecklistItem>
                                        <ChecklistItem>
                                            {t('eshop.transport.cascoPackageOptions.eko3.collision')}
                                        </ChecklistItem>
                                        <ChecklistItem>
                                            {t('eshop.transport.cascoPackageOptions.eko3.theft')}
                                        </ChecklistItem>
                                        <ChecklistItem>
                                            {t('eshop.transport.cascoPackageOptions.eko3.vandalism')}
                                        </ChecklistItem>
                                        <ChecklistItem>
                                            {t('eshop.transport.cascoPackageOptions.eko3.unexpected')}
                                        </ChecklistItem>
                                    </div>

                                    <Paragraph isBold>
                                        {t('eshop.transport.cascoPackageOptions.eko3.standardBenefitsTitle')}
                                    </Paragraph>
                                    <div className="flex flex-col gap-1">
                                        <ChecklistItem>
                                            {t('eshop.transport.cascoPackageOptions.eko3.replacementCarTransport')}
                                        </ChecklistItem>
                                        <ChecklistItem>
                                            {t('eshop.transport.cascoPackageOptions.eko3.frontGlass')}
                                        </ChecklistItem>
                                        <ChecklistItem>
                                            {t('eshop.transport.cascoPackageOptions.eko3.bodyRepair')}
                                        </ChecklistItem>
                                        <ChecklistItem>
                                            {t('eshop.transport.cascoPackageOptions.eko3.chargingStationBasic')}
                                        </ChecklistItem>
                                    </div>
                                    <TextButton
                                        preventDefault
                                        href={parameters.eshopInfoUrls.comparePolicies}
                                        target="_blank"
                                        rightAddon={<ArrowRightIcon className="size-4" />}>
                                        {t('general.readMore')}
                                    </TextButton>
                                </div>
                            )}
                        </div>
                    ),
                };
            });
    }, [insuranceDuration, parameters.eshopInfoUrls.comparePolicies, premiums, t]);

    return options;
};
